/**
 * 控制台页面表（总纲 §6）：路径 → 所需权限点，路由表与权限测试共用这一份。
 *
 * `cap` 为空表示登录即可（消息 / 个人 / 帮助）。
 * 路由级 Guard 与 `can()` 只管显隐，服务端是真源（spec 00 §3.2-9）。
 */
import type { Capability } from '@/lib/permission';

export interface PageDef {
  path: string;
  cap?: Capability;
  /** 这一页由哪些旧路径合并而来（文档用；重定向真源在 redirects.ts） */
  absorbs?: readonly string[];
}

export const PAGES: readonly PageDef[] = [
  { path: '/today', cap: 'employee:read', absorbs: ['/today', '/data/report'] },
  { path: '/employees', cap: 'employee:read', absorbs: ['/employees'] },
  { path: '/employees/hire', cap: 'employee:write', absorbs: ['/catalog'] },
  /** 员工主页容器；三面各自的 cap 按 §6「按面」收窄，由容器决定哪几面可见 */
  { path: '/employees/:id', cap: 'employee:read' },
  { path: '/employees/:id/report', cap: 'report:read' },
  { path: '/employees/:id/lists', cap: 'lead:read' },
  { path: '/employees/:id/brief', cap: 'employee:read' },
  { path: '/customers', cap: 'employee:read' },
  { path: '/customers/leads', cap: 'lead:read', absorbs: ['/leads', '/blacklist'] },
  { path: '/customers/calls', cap: 'call:read', absorbs: ['/data/calls'] },
  { path: '/customers/intents', cap: 'call:read', absorbs: ['/data/intentions'] },
  { path: '/work', cap: 'campaign:read', absorbs: ['/campaigns'] },
  { path: '/billing', cap: 'finance:read', absorbs: ['/finance/capital'] },
  { path: '/billing/seats', cap: 'finance:read', absorbs: ['/finance/seats'] },
  { path: '/billing/bills', cap: 'finance:read', absorbs: ['/finance/settlement'] },
  { path: '/billing/invoices', cap: 'finance:read', absorbs: ['/finance/invoices'] },
  { path: '/settings', cap: 'tenant:read' },
  { path: '/settings/company', cap: 'tenant:read', absorbs: ['/tenant/certification'] },
  { path: '/settings/members', cap: 'member:read', absorbs: ['/tenant/members'] },
  { path: '/settings/resources/lines', cap: 'resource:read', absorbs: ['/resources/lines'] },
  { path: '/settings/resources/sms', cap: 'resource:read', absorbs: ['/resources/sms'] },
  { path: '/settings/resources/wechat', cap: 'resource:read', absorbs: ['/resources/wechat'] },
  { path: '/settings/developer', cap: 'apikey:write', absorbs: ['/system/api-keys'] },
  { path: '/settings/exports', cap: 'export:write', absorbs: ['/system/exports'] },
  { path: '/settings/logs', cap: 'audit:read', absorbs: ['/system/logs'] },
  { path: '/inbox', absorbs: ['/system/messages'] },
  { path: '/account/profile', absorbs: ['/account/profile'] },
  { path: '/help', absorbs: ['/help'] },
];

export const pageCap = (path: string): Capability | undefined =>
  PAGES.find((p) => p.path === path)?.cap;
